
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Store, Users, MapPin, Clock, TrendingUp, ShoppingBag } from 'lucide-react';

const RetailPEO = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-pink-50">
      <Helmet>
        <title>PEO Services for Retail Companies & Stores | The PEO App</title>
        <meta name="description" content="Specialized PEO services for retail companies, stores, and chains. Expert multi-location management, seasonal hiring, and retail-specific HR solutions." />
        <link rel="canonical" href="https://thepeoapp.com/industries/retail" />
        <meta name="keywords" content="retail PEO, store PEO, retail chain HR, multi-location PEO, seasonal hiring, retail workers comp, retail payroll services" />
        
        <meta property="og:title" content="PEO Services for Retail Companies & Stores" />
        <meta property="og:description" content="Specialized PEO solutions for retail with multi-location and seasonal workforce expertise." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries/retail" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Store className="h-16 w-16 text-pink-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              PEO Services for Retail Companies
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Specialized Professional Employer Organization services for retail stores, chains, 
              and e-commerce businesses. Expert multi-location management, seasonal hiring, and retail HR solutions.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Retail Industry Challenges</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Multi-Location Management</h3>
                    <p className="text-gray-700">Consistent HR policies across multiple store locations and states</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <TrendingUp className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Seasonal Workforce Fluctuations</h3>
                    <p className="text-gray-700">Managing holiday hiring surges and post-season layoffs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">High Part-Time Workforce</h3>
                    <p className="text-gray-700">Managing benefits and scheduling for predominantly part-time staff</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Clock className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Extended Operating Hours</h3>
                    <p className="text-gray-700">Managing staff for early morning to late night operations</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <ShoppingBag className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Customer Service Training</h3>
                    <p className="text-gray-700">Consistent customer service standards across all locations</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Store className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Inventory & Loss Prevention</h3>
                    <p className="text-gray-700">Employee training on theft prevention and inventory management</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Retail-Specialized PEO Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-pink-50 p-6 rounded-xl">
                <MapPin className="h-12 w-12 text-pink-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Multi-Location Support</h3>
                <p className="text-gray-700">
                  Centralized HR management with local compliance across 
                  multiple store locations and different state regulations.
                </p>
              </div>
              
              <div className="bg-pink-50 p-6 rounded-xl">
                <TrendingUp className="h-12 w-12 text-pink-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Seasonal Hiring Programs</h3>
                <p className="text-gray-700">
                  Streamlined processes for rapid holiday hiring and 
                  off-season workforce adjustments with compliance support.
                </p>
              </div>
              
              <div className="bg-pink-50 p-6 rounded-xl">
                <Users className="h-12 w-12 text-pink-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Part-Time Benefits</h3>
                <p className="text-gray-700">
                  Flexible benefits programs designed for part-time retail workers 
                  to improve retention and satisfaction.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find Retail-Specialized PEOs</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand retail operations 
              and have proven experience managing multi-location retail workforces.
            </p>
            <Link to="/application">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Retail PEO Quotes
              </Button>
            </Link>
          </div>

          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Retail Businesses We Serve</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Clothing & Apparel</h3>
                <p className="text-sm text-gray-600">Fashion retailers, department stores</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Grocery & Food</h3>
                <p className="text-sm text-gray-600">Supermarkets, convenience stores</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Electronics</h3>
                <p className="text-sm text-gray-600">Consumer electronics, mobile stores</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Specialty Retail</h3>
                <p className="text-sm text-gray-600">Home goods, automotive, sports</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RetailPEO;
